import {
  CameraSettingsRotate1Bold,
  ModernTV3DSyncBold,
  PictureSunBold,
  PinLocation1Bold,
  RealEstateDimensionsBlockBold,
  VideoPlayerSliderBold,
} from '@ha/icons';
import React from 'react';
import { makeStyles } from 'tss-react/mui';

import { ListingPhoto } from '@ha/api/v2/types/Listing';
import { VerticalButton } from 'ha/ui/VerticalButton';
import { useIntl } from 'ha/i18n';
import { Grid, BreadcrumbsWrapper, units } from '@hbf/dsl';

import { ListingAddress } from './ListingAddress';
import { GalleryGridSection } from './GalleryGridSection';
import { MapSection } from './MapSection';
import { ListingActions } from './ListingActions';
import { ListingPrice } from '../fragments/ListingPrice';
import { ListingBreadcrumbs } from '../fragments/ListingBreadcrumbs';
import { usePageRedesign } from '../hooks/usePageRedesign';
import { ListingInfo } from './ListingInfo';
import { GalleryDesktopTiles } from './GalleryDesktopTiles';

const useStyles = makeStyles()(theme => ({
  container: {
    margin: 0,
    width: '100%',
  },
  heightRedesign: {
    height: '100%',
  },
  imageGrid: {
    marginTop: units.rem(1),
    marginBottom: units.rem(1.5),
    position: 'relative',
  },
  topGridActionsContainer: {
    width: 'auto',
  },
  topGridActionsContainerRedesign: {
    position: 'absolute',
    top: units.rem(0.75),
    right: units.rem(1),
    width: 'auto',
    zIndex: 9,
  },
  mediaButton: {
    display: 'inline',
    marginRight: units.rem(0.75),
    '&:last-of-type': {
      marginRight: 0,
    },
  },
  tiles: {
    marginTop: theme.spacing(1),
  },
  listingInfo: {
    marginTop: theme.spacing(2),
    marginLeft: -theme.spacing(1),
  },
}));

export interface Props {
  photos: ListingPhoto[];
  floorplanPhotos: ListingPhoto[];
  panoramicPhotos: ListingPhoto[];
  onImageButtonClick: () => void;
  onPanoramicPhotosButtonClick: () => void;
  onVideoButtonClick: () => void;
  onFloorplanButtonClick: () => void;
  onMapButtonClick: () => void;
  onVirtualTourButtonClick: () => void;
  imageGridProps: {
    tiles: ListingPhoto[];
    onImageClick: (id: number) => void;
  };
  virtualTourLink?: string;
  street: string;
  city: string;
  country: string;
  listingId: number;
  numVideos: number;
  hasCoordinates: boolean;
  showDefaultMediaType: boolean;
}

export const GalleryDesktopLayout: React.FC<Props> = ({
  photos,
  floorplanPhotos,
  panoramicPhotos,
  imageGridProps,
  onImageButtonClick,
  onPanoramicPhotosButtonClick,
  onVideoButtonClick,
  onFloorplanButtonClick,
  onMapButtonClick,
  onVirtualTourButtonClick,
  virtualTourLink,
  street,
  city,
  country,
  numVideos,
  hasCoordinates,
  showDefaultMediaType,
}) => {
  const { T } = useIntl();
  const { classes, cx } = useStyles();
  const withPageRedesign = usePageRedesign();

  return (
    <Grid
      container
      justifyContent="space-between"
      className={cx(classes.container, {
        [classes.heightRedesign]: withPageRedesign,
      })}
      data-test-locator="GalleryDesktopLayout"
    >
      {!withPageRedesign ? (
        <Grid container item justifyContent="space-between" alignItems="center">
          <Grid item>
            <ListingAddress
              street={street}
              city={city}
              country={country}
              isMapAvailable={hasCoordinates}
              onAddressClick={
                showDefaultMediaType ? undefined : onMapButtonClick
              }
              showBreadcrumbs
            />
          </Grid>

          <div className={classes.topGridActionsContainer}>
            <ListingActions />
          </div>
        </Grid>
      ) : (
        <Grid container>
          <BreadcrumbsWrapper elevation={0} padLeft={false} padBottom={false}>
            <ListingBreadcrumbs />
          </BreadcrumbsWrapper>
        </Grid>
      )}
      <Grid
        item
        xs={12}
        className={cx(classes.imageGrid, {
          [classes.heightRedesign]: withPageRedesign,
        })}
      >
        {showDefaultMediaType && hasCoordinates ? (
          <MapSection />
        ) : (
          <GalleryGridSection imageGridProps={imageGridProps} />
        )}
        {withPageRedesign && (
          <div
            className={classes.topGridActionsContainerRedesign}
            data-test-locator="GalleryDesktioLayout/ListingActions"
          >
            <ListingActions />
          </div>
        )}
        {withPageRedesign && (
          <div className={classes.tiles}>
            <GalleryDesktopTiles
              country={country}
              onFloorplanButtonClick={onFloorplanButtonClick}
              onImageClick={imageGridProps.onImageClick}
              onPanoramicPhotosButtonClick={onPanoramicPhotosButtonClick}
              onVideoButtonClick={onVideoButtonClick}
              onVirtualTourButtonClick={onVirtualTourButtonClick}
              numVideos={numVideos}
              photos={photos}
              floorplanPhotos={floorplanPhotos}
              panoramicPhotos={panoramicPhotos}
              virtualTourLink={virtualTourLink}
            />
          </div>
        )}
        {withPageRedesign && (
          <div className={classes.listingInfo}>
            <ListingInfo
              onMapBtnClick={hasCoordinates ? onMapButtonClick : undefined}
            />
          </div>
        )}
      </Grid>
      {!withPageRedesign && (
        <Grid container item justifyContent="space-between" alignItems="center">
          <Grid item>
            <ListingPrice variant="desktop-gallery" />
          </Grid>
          <Grid item>
            {photos.length > 0 && (
              <div className={classes.mediaButton}>
                <VerticalButton
                  data-test-locator="Photo button"
                  icon={<PictureSunBold />}
                  text={T('listings.media.photo')}
                  onClick={onImageButtonClick}
                />
              </div>
            )}
            {panoramicPhotos.length > 0 && (
              <div className={classes.mediaButton}>
                <VerticalButton
                  data-test-locator="Panoramic photo button"
                  icon={<CameraSettingsRotate1Bold />}
                  text={T('listings.media.panoramic_photo')}
                  onClick={onPanoramicPhotosButtonClick}
                />
              </div>
            )}
            {virtualTourLink && (
              <div className={classes.mediaButton}>
                <VerticalButton
                  data-test-locator="Virtual tour button"
                  icon={<ModernTV3DSyncBold />}
                  text={T('listing_gallery_3D_tour')}
                  onClick={onVirtualTourButtonClick}
                />
              </div>
            )}
            {numVideos > 0 && (
              <div className={classes.mediaButton}>
                <VerticalButton
                  data-test-locator="Video button"
                  icon={<VideoPlayerSliderBold />}
                  text={T('listings.media.video')}
                  onClick={onVideoButtonClick}
                />
              </div>
            )}
            {floorplanPhotos.length > 0 && (
              <div className={classes.mediaButton}>
                <VerticalButton
                  data-test-locator="Floor Plan button"
                  icon={<RealEstateDimensionsBlockBold />}
                  text={T('listings.media.floorplan')}
                  onClick={onFloorplanButtonClick}
                />
              </div>
            )}
            {hasCoordinates && !showDefaultMediaType && (
              <div className={classes.mediaButton}>
                <VerticalButton
                  data-test-locator="Map button"
                  icon={<PinLocation1Bold />}
                  text={T('listings.media.map')}
                  onClick={onMapButtonClick}
                />
              </div>
            )}
          </Grid>
        </Grid>
      )}
    </Grid>
  );
};
